import React, { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import {
	Button,
	Callout,
	Card,
	FormGroup,
	Icon,
	InputGroup,
	Intent,
	NonIdealState,
	Switch,
	Tag,
	TextArea,
} from "@blueprintjs/core";
import Select from "react-select";
import clsx from "clsx";
import { IStore } from "redux/store";
import { commonUtils } from "redux/_common";
import authConstants from "redux/app/auth/constants";
import toolSelectors from "redux/app/tool/selectors";
import toolActions from "redux/app/tool/actions";
import { getTenantsOptionList, getVesselsOptionList } from "redux/app/tool/utils";
import { IOption } from "redux/app/feedback/@types";
import {
	clearIndicatorStyles,
	controlStyles,
	dropdownIndicatorStyles,
	menuStyles,
	optionStyles,
	placeholderStyles,
} from "redux/app/feedback/utils";

type OwnProps = {};
type CNRPatchesProps = PropsFromRedux & OwnProps;

type ToolAction = "applyPatches" | "createBunkerReports" | "deleteOldDocs";

type ExecutionState = {
	action: ToolAction;
	status: number;
	payload: any;
};

type PayloadValidation = {
	isValid: boolean;
	message: string;
	itemCount: number;
};

const APPLY_PATCHES_TEMPLATE = JSON.stringify(
	[
		{
			SignalID: 123456,
			ExternalID: "cnr::example-doc-id",
			data: {
				me_ifo_bdn_reference: "BDN-001",
				ifo_lifted: "12.5",
			},
		},
	],
	null,
	2
);

const CREATE_BUNKER_TEMPLATE = JSON.stringify(
	[
		{
			bdn_number: "BDN-001",
			imo: "1234567",
			bunker_delivery_datetime: "2025-01-10T10:30:00",
			vlsfo_mass: "120.5",
			vlsfo_lcv: "41.8",
		},
	],
	null,
	2
);

function toOptionalInt(raw: string): number | undefined {
	const trimmed = raw.trim();
	if (!trimmed) return undefined;
	const n = Number(trimmed);
	if (!Number.isFinite(n)) return undefined;
	return Math.floor(n);
}

function validatePayloadArray(raw: string, label: string): PayloadValidation {
	const trimmed = raw.trim();
	if (!trimmed) {
		return {
			isValid: false,
			message: `${label} payload is empty.`,
			itemCount: 0,
		};
	}

	try {
		const parsed = JSON.parse(trimmed);
		if (!Array.isArray(parsed)) {
			return {
				isValid: false,
				message: `${label} payload must be a JSON array.`,
				itemCount: 0,
			};
		}
		if (parsed.length === 0) {
			return {
				isValid: false,
				message: `${label} payload must be a non-empty array.`,
				itemCount: 0,
			};
		}
		return {
			isValid: true,
			message: `${parsed.length} item${parsed.length > 1 ? "s" : ""} ready.`,
			itemCount: parsed.length,
		};
	} catch (e) {
		return {
			isValid: false,
			message: `${label} payload must be valid JSON.`,
			itemCount: 0,
		};
	}
}

function CNRPatches(props: CNRPatchesProps) {
	const [applyPayloadText, setApplyPayloadText] = useState(APPLY_PATCHES_TEMPLATE);
	const [applyBatchSize, setApplyBatchSize] = useState("200");
	const [applySkip, setApplySkip] = useState("0");

	const [bunkerPayloadText, setBunkerPayloadText] = useState(CREATE_BUNKER_TEMPLATE);
	const [bunkerBatchSize, setBunkerBatchSize] = useState("200");
	const [bunkerSkip, setBunkerSkip] = useState("0");

	const [selectedTenant, setSelectedTenant] = useState<IOption | undefined>();
	const [selectedVessel, setSelectedVessel] = useState<IOption | undefined>();
	const [deleteCutoffDateIso, setDeleteCutoffDateIso] = useState(
		"2024-12-31T00:00:00.000Z"
	);
	const [deleteDryRun, setDeleteDryRun] = useState(true);
	const [selectedTool, setSelectedTool] = useState<ToolAction>("applyPatches");

	const [runningAction, setRunningAction] = useState<ToolAction | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [execution, setExecution] = useState<ExecutionState | null>(null);

	const isRunning = runningAction !== null;
	const applyPayloadValidation = useMemo(
		() => validatePayloadArray(applyPayloadText, "Patch CNR Data"),
		[applyPayloadText]
	);
	const bunkerPayloadValidation = useMemo(
		() => validatePayloadArray(bunkerPayloadText, "Create Bunker Report"),
		[bunkerPayloadText]
	);

	const actionTagIntent = useMemo(() => {
		if (!execution) return Intent.NONE;
		if (execution.status >= 200 && execution.status < 300) return Intent.SUCCESS;
		return Intent.DANGER;
	}, [execution]);

	useEffect(() => {
		props.loadTenantsList();
	}, []);

	useEffect(() => {
		if (!selectedTenant?.value) {
			setSelectedVessel(undefined);
			return;
		}
		props.loadTenantSpecificVesselsList(selectedTenant.value);
		setSelectedVessel(undefined);
	}, [selectedTenant?.value]);

	const execute = async ({
		action,
		path,
		query,
		body,
	}: {
		action: ToolAction;
		path: string;
		query?: Record<string, string>;
		body: any;
	}) => {
		setErrorMessage(null);
		setExecution(null);
		setRunningAction(action);

		try {
			const qs = new URLSearchParams(query || {}).toString();
			const url = `${commonUtils.getAPIBaseUrl()}${path}${qs ? `?${qs}` : ""}`;
			const resp = await fetch(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					[authConstants.headers.TOKEN]: props.authToken || "",
				},
				body: JSON.stringify(body),
			});

			let payload: any = null;
			try {
				payload = await resp.json();
			} catch (jsonError) {
				payload = { message: "Response was not valid JSON" };
			}

			setExecution({
				action,
				status: resp.status,
				payload,
			});
			if (!resp.ok) {
				setErrorMessage(payload?.message || `Request failed with ${resp.status}`);
			}
		} catch (e: any) {
			setErrorMessage(e?.message || "Network error");
		} finally {
			setRunningAction(null);
		}
	};

	const runApplyPatches = async () => {
		let payload: any;
		try {
			payload = JSON.parse(applyPayloadText);
		} catch (e) {
			setErrorMessage("Patch CNR Data payload must be valid JSON.");
			return;
		}
		if (!Array.isArray(payload) || payload.length === 0) {
			setErrorMessage("Patch CNR Data payload must be a non-empty array.");
			return;
		}

		const batchSize = toOptionalInt(applyBatchSize);
		const skip = toOptionalInt(applySkip);
		const query: Record<string, string> = {};
		if (batchSize !== undefined) query.batchSize = String(batchSize);
		if (skip !== undefined) query.skip = String(Math.max(0, skip));

		await execute({
			action: "applyPatches",
			path: "/api/apply-cnr-patches",
			query,
			body: payload,
		});
	};

	const runCreateBunkerReports = async () => {
		let payload: any;
		try {
			payload = JSON.parse(bunkerPayloadText);
		} catch (e) {
			setErrorMessage("Create Bunker Report payload must be valid JSON.");
			return;
		}
		if (!Array.isArray(payload) || payload.length === 0) {
			setErrorMessage("Create Bunker Report payload must be a non-empty array.");
			return;
		}

		const batchSize = toOptionalInt(bunkerBatchSize);
		const skip = toOptionalInt(bunkerSkip);
		const query: Record<string, string> = {};
		if (batchSize !== undefined) query.batchSize = String(batchSize);
		if (skip !== undefined) query.skip = String(Math.max(0, skip));

		await execute({
			action: "createBunkerReports",
			path: "/api/create-bunker-reports-v2",
			query,
			body: payload,
		});
	};

	const runDeleteOldDocs = async () => {
		if (!selectedVessel?.value) {
			setErrorMessage("Please select a tenant and vessel before deleting old CNR docs.");
			return;
		}
		if (!deleteDryRun) {
			const confirmed = window.confirm(
				`You are about to permanently delete old CNR docs for vessel ${selectedVessel.value}. Continue?`
			);
			if (!confirmed) return;
		}
		const body = {
			vesselId: selectedVessel.value,
			cutoffDateIso: deleteCutoffDateIso.trim() || undefined,
			dryRun: deleteDryRun,
		};

		await execute({
			action: "deleteOldDocs",
			path: "/api/delete-old-cnr-docs",
			body,
		});
	};

	return (
		<div className="min-h-[calc(100vh-98px)] bg-gray-50 p-3 sm:p-5 md:p-8 lg:p-12">
			<div className="max-w-[1600px] mx-auto space-y-6">
				<div>
					<h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2 sm:gap-3">
						
						<Icon icon="changes" size={28} className="text-blue-600 hidden sm:block" />
						CNR Patches
					</h1>
					<p className="text-gray-600">
						Run controlled CNR utilities for patching reports, creating bunker
						reports, and deleting old CNR docs.
					</p>
				</div>

				<Callout intent={Intent.WARNING} icon="warning-sign" title="Use Carefully">
					These actions write directly to CNR documents. Start with small batches
					and use dry run where available.
				</Callout>
				<Callout intent={Intent.PRIMARY} icon="manual" title="Quick Steps">
					1) Click <strong>Load Example</strong>, 2) replace with your real data,
					3) keep batch size small for first run, 4) check the result panel before
					running the next batch.
				</Callout>

				{errorMessage && (
					<Callout intent={Intent.DANGER} icon="error" title="Error">
						{errorMessage}
					</Callout>
				)}

				<div className="grid grid-cols-1 xl:grid-cols-[16rem_minmax(0,1fr)] items-start gap-4 xl:gap-6">
					<Card
						elevation={1}
						className="bg-white h-fit w-full xl:sticky xl:top-6"
					>
						<div className="p-2 space-y-2">
							<h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
								CNR Tools
							</h2>
							<Button
								fill
								alignText="left"
								icon="edit"
								intent={selectedTool === "applyPatches" ? Intent.PRIMARY : Intent.NONE}
								onClick={() => setSelectedTool("applyPatches")}
							>
								Patch CNR Data
							</Button>
							<Button
								fill
								alignText="left"
								icon="tint"
								intent={
									selectedTool === "createBunkerReports"
										? Intent.PRIMARY
										: Intent.NONE
								}
								onClick={() => setSelectedTool("createBunkerReports")}
							>
								Create Bunker Report
							</Button>
							<Button
								fill
								alignText="left"
								icon="trash"
								intent={selectedTool === "deleteOldDocs" ? Intent.PRIMARY : Intent.NONE}
								onClick={() => setSelectedTool("deleteOldDocs")}
							>
								Delete Old CNR Docs
							</Button>
						</div>
					</Card>

					<div className="space-y-6 flex-1 min-w-0">
						{selectedTool === "applyPatches" && (
							<Card elevation={1} className="bg-white">
								<div className="p-2">
									<h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
										<Icon icon="edit" className="text-gray-500" />
										1) Patch CNR Data
									</h2>
									<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
										<FormGroup label="batchSize" className="mb-0">
											<InputGroup
												placeholder="200"
												value={applyBatchSize}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													setApplyBatchSize(e.target.value)
												}
												disabled={isRunning}
											/>
										</FormGroup>
										<FormGroup label="skip" className="mb-0">
											<InputGroup
												placeholder="0"
												value={applySkip}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													setApplySkip(e.target.value)
												}
												disabled={isRunning}
											/>
										</FormGroup>
									</div>
									<FormGroup
										label="Payload (JSON array)"
										helperText="Paste the patch list shared by engineering (JSON array)."
										className="mt-4"
									>
										<TextArea
											fill
											rows={12}
											value={applyPayloadText}
											onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
												setApplyPayloadText(e.target.value)
											}
											disabled={isRunning}
										/>
									</FormGroup>
									<div className="mb-4">
										<Tag
											intent={
												applyPayloadValidation.isValid ? Intent.SUCCESS : Intent.WARNING
											}
											minimal
										>
											{applyPayloadValidation.message}
										</Tag>
									</div>
									<div className="flex flex-col sm:flex-row gap-2">
										<Button
											icon="duplicate"
											disabled={isRunning}
											onClick={() => setApplyPayloadText(APPLY_PATCHES_TEMPLATE)}
										>
											Load Example
										</Button>
										<Button
											intent={Intent.PRIMARY}
											icon="play"
											loading={runningAction === "applyPatches"}
											disabled={isRunning || !applyPayloadValidation.isValid}
											onClick={runApplyPatches}
										>
											{runningAction === "applyPatches"
												? "Running Patch CNR Data..."
												: "Run Patch CNR Data"}
										</Button>
									</div>
								</div>
							</Card>
						)}

						{selectedTool === "createBunkerReports" && (
							<Card elevation={1} className="bg-white">
								<div className="p-2">
									<h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
										<Icon icon="tint" className="text-gray-500" />
										2) Create Bunker Report
									</h2>
									<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
										<FormGroup label="batchSize" className="mb-0">
											<InputGroup
												placeholder="200"
												value={bunkerBatchSize}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													setBunkerBatchSize(e.target.value)
												}
												disabled={isRunning}
											/>
										</FormGroup>
										<FormGroup label="skip" className="mb-0">
											<InputGroup
												placeholder="0"
												value={bunkerSkip}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													setBunkerSkip(e.target.value)
												}
												disabled={isRunning}
											/>
										</FormGroup>
										<FormGroup label="Tip" className="mb-0">
											<div className="h-[34px] flex items-center text-sm text-gray-600">
												Use small first batch (e.g. 20) to validate.
											</div>
										</FormGroup>
									</div>
									<FormGroup
										label="Payload (JSON array)"
										helperText="Paste bunker records from Signal (JSON array)."
										className="mt-4"
									>
										<TextArea
											fill
											rows={12}
											value={bunkerPayloadText}
											onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
												setBunkerPayloadText(e.target.value)
											}
											disabled={isRunning}
										/>
									</FormGroup>
									<div className="mb-4">
										<Tag
											intent={
												bunkerPayloadValidation.isValid ? Intent.SUCCESS : Intent.WARNING
											}
											minimal
										>
											{bunkerPayloadValidation.message}
										</Tag>
									</div>
									<div className="flex flex-col sm:flex-row gap-2">
										<Button
											icon="duplicate"
											disabled={isRunning}
											onClick={() => setBunkerPayloadText(CREATE_BUNKER_TEMPLATE)}
										>
											Load Example
										</Button>
										<Button
											intent={Intent.PRIMARY}
											icon="play"
											loading={runningAction === "createBunkerReports"}
											disabled={isRunning || !bunkerPayloadValidation.isValid}
											onClick={runCreateBunkerReports}
										>
											{runningAction === "createBunkerReports"
												? "Running Create Bunker Report..."
												: "Run Create Bunker Report"}
										</Button>
									</div>
								</div>
							</Card>
						)}

						{selectedTool === "deleteOldDocs" && (
							<Card elevation={1} className="bg-white">
								<div className="p-2">
									<h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
										<Icon icon="trash" className="text-gray-500" />
										3) Delete Old CNR Docs
									</h2>
									<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
										<FormGroup label="Tenant (required)" className="mb-0">
											<Select
												isClearable
												isSearchable
												placeholder="Select tenant..."
												isDisabled={isRunning}
												value={selectedTenant}
												options={getTenantsOptionList(props.tenants)}
												isLoading={props.jobToLoadTenants.isLoading}
												onChange={(newValue) => setSelectedTenant(newValue ?? undefined)}
												menuPosition="fixed"
												className="w-full text-sm"
												classNames={{
													option: ({ isFocused, isSelected }) =>
														clsx(
															isFocused && optionStyles.focus,
															isSelected && optionStyles.selected,
															optionStyles.base
														),
													dropdownIndicator: () => dropdownIndicatorStyles,
													menu: () => menuStyles,
													clearIndicator: () => clearIndicatorStyles,
													placeholder: () => placeholderStyles,
													control: ({ isFocused, isDisabled }) =>
														clsx(
															isFocused ? controlStyles.focus : controlStyles.nonFocus,
															isDisabled ? controlStyles.disabled : controlStyles.base
														),
													valueContainer: ({ isDisabled }) =>
														clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
												}}
											/>
										</FormGroup>
										<FormGroup label="Vessel (required)" className="mb-0">
											<Select
												isClearable
												isSearchable
												placeholder={
													selectedTenant?.value
														? "Select vessel..."
														: "Select tenant first"
												}
												isDisabled={isRunning || !selectedTenant?.value}
												value={selectedVessel}
												options={getVesselsOptionList(props.vessels)}
												isLoading={props.jobToLoadVesselsSpecificToTenant.isLoading}
												onChange={(newValue) => setSelectedVessel(newValue ?? undefined)}
												menuPosition="fixed"
												className="w-full text-sm"
												classNames={{
													option: ({ isFocused, isSelected }) =>
														clsx(
															isFocused && optionStyles.focus,
															isSelected && optionStyles.selected,
															optionStyles.base
														),
													dropdownIndicator: () => dropdownIndicatorStyles,
													menu: () => menuStyles,
													clearIndicator: () => clearIndicatorStyles,
													placeholder: () => placeholderStyles,
													control: ({ isFocused, isDisabled }) =>
														clsx(
															isFocused ? controlStyles.focus : controlStyles.nonFocus,
															isDisabled ? controlStyles.disabled : controlStyles.base
														),
													valueContainer: ({ isDisabled }) =>
														clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
												}}
											/>
										</FormGroup>
										<FormGroup label="cutoffDateIso (optional)" className="mb-0">
											<InputGroup
												placeholder="2024-12-31T00:00:00.000Z"
												value={deleteCutoffDateIso}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													setDeleteCutoffDateIso(e.target.value)
												}
												disabled={isRunning}
											/>
										</FormGroup>
									</div>
									<div className="mt-4 text-sm text-gray-600">
										Selected Vessel ID:{" "}
										<span className="font-mono">{selectedVessel?.value || "—"}</span>
									</div>
									<div className="mt-4">
										<Switch
											checked={deleteDryRun}
											disabled={isRunning}
											label="Dry run (recommended first)"
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setDeleteDryRun(e.target.checked)
											}
										/>
									</div>
									{!deleteDryRun && (
										<Callout
											intent={Intent.DANGER}
											icon="warning-sign"
											className="mt-4"
											title="Live delete mode"
										>
											This will permanently remove matching CNR docs. You will be asked to
											confirm before execution.
										</Callout>
									)}
									<div className="flex flex-col sm:flex-row gap-2 mt-2">
										<Button
											intent={deleteDryRun ? Intent.PRIMARY : Intent.DANGER}
											icon="play"
											loading={runningAction === "deleteOldDocs"}
											disabled={isRunning || !selectedVessel?.value}
											onClick={runDeleteOldDocs}
										>
											{runningAction === "deleteOldDocs"
												? "Running Delete Old CNR Docs..."
												: "Run Delete Old CNR Docs"}
										</Button>
									</div>
								</div>
							</Card>
						)}
					</div>
				</div>

				{execution ? (
					<Card elevation={1} className="bg-white">
						<div className="p-2 space-y-4">
							<div className="flex flex-wrap items-center gap-2 sm:gap-3">
								<Tag intent={actionTagIntent} large>
									HTTP {execution.status}
								</Tag>
								<Tag minimal>
									Action:{" "}
									{execution.action === "applyPatches"
										? "Patch CNR Data"
										: execution.action === "createBunkerReports"
											? "Create Bunker Report"
											: "Delete Old CNR Docs"}
								</Tag>
							</div>
							<pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-auto text-xs">
								{JSON.stringify(execution.payload, null, 2)}
							</pre>
						</div>
					</Card>
				) : (
					<NonIdealState
						icon="history"
						title="No execution yet"
						description="Run any action to see request results here."
						className="py-8 bg-white rounded-lg border border-gray-100"
					/>
				)}
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const toolStore = toolSelectors._getStore(store);
	return {
		authToken: store.app.auth.authToken,
		tenants: toolSelectors.getTenants(toolStore),
		vessels: toolSelectors.getVessels(toolStore),
		jobToLoadTenants: toolSelectors.jobToLoadTenants(toolStore),
		jobToLoadVesselsSpecificToTenant:
			toolSelectors.jobToLoadVesselsSpecificToTenant(toolStore),
	};
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		loadTenantsList() {
			dispatch(toolActions.commands.toolTenantListLoad());
		},
		loadTenantSpecificVesselsList(tenantId: string) {
			dispatch(toolActions.commands.toolTenantSpecificVesselLoad(tenantId));
		},
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(CNRPatches);

